import React, { useEffect, useRef, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import API from '../../../../services/API'

const statusStyles = {
  Available: { color: '#00964C', bg: 'rgba(0,150,76,0.28)' },
  'Off Duty': { color: '#475569', bg: 'rgba(71,85,105,0.22)' },
  'Manager On Call': { color: '#1F3D7C', bg: 'rgba(31,61,124,0.26)' },
  'Mobile & Available': { color: '#157746', bg: '#15774647' },
  'C1 Only': { color: '#0e4f2e', bg: '#0e4f2e47' },
  'C1 & C2 Only': { color: '#0e4f2e', bg: '#0e4f2e47' },
  Break: { color: '#1F3D7C', bg: 'rgba(31,61,124,0.26)' },
  Unavailable: { color: '#85276A', bg: 'rgba(133,39,106,0.25)' },
  Assigned: { color: '#E32525', bg: 'rgba(227,37,37,0.26)' },
  'On Route': { color: '#9A3412', bg: 'rgba(154,52,18,0.24)' },
  'At Scene': { color: '#CCA43B', bg: 'rgba(204,164,59,0.26)' },
  'On Scene': { color: '#CCA43B', bg: 'rgba(204,164,59,0.26)' },
  'Leave Scene': { color: '#CCA43B', bg: 'rgba(204,164,59,0.26)' },
  'At Hosp': { color: '#CCA43B', bg: 'rgba(204,164,59,0.26)' },
  Handover: { color: '#CCA43B', bg: 'rgba(204,164,59,0.26)' },
}

// Mirrors server resourceStatusRules.js: Off Duty resources are listed so the
// dispatcher can see them, but can't be picked — the server 409s regardless.
const isOffDuty = (resource) => resource?.status === 'Off Duty'

const AssignResource = ({
  open,
  onClose,
  controlRoomID,
  incident,
  data,
  setSelectedIncident,
}) => {
  const [resourceOptions, setResourceOptions] = useState(null)
  const [selectedResource, setSelectedResource] = useState(null)
  const [assigning, setAssigning] = useState(false)
  const [assignError, setAssignError] = useState('')
  const [crossRoomConflict, setCrossRoomConflict] = useState(null)
  const resourceInputRef = useRef(null)

  // Statuses the assign silently flips to Assigned/Available. Off Duty is
  // deliberately not here — it can't be assigned at all (see isOffDuty).
  const unavailableStatuses = ['Break', 'Unavailable']

  useEffect(() => {
    setSelectedResource(null)
    setAssignError('')
  }, [open])

  // Same-control-room conflicts ride along in data.resources already
  // (assignedIncident), but a crew member tasked via a DIFFERENT control
  // room isn't in that list at all — a separate check is the only way to
  // warn before silently standing them down elsewhere on assign.
  useEffect(() => {
    setCrossRoomConflict(null)
    if (!selectedResource?.id) return undefined
    if (selectedResource.assignedIncident === incident?.id) return undefined

    let cancelled = false
    API.get(
      `/commandcontrol/resources/${selectedResource.id}/incident-conflict`,
    ).then((res) => {
      if (cancelled || res.status !== 200) return
      setCrossRoomConflict(res.data?.conflict ?? null)
    })

    return () => {
      cancelled = true
    }
  }, [selectedResource, incident?.id])

  useEffect(() => {
    if (!open) return undefined

    const focusTimer = setTimeout(() => {
      resourceInputRef.current?.focus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [open])

  useEffect(() => {
    if (data?.resources?.length) {
      setResourceOptions(data.resources)
    } else {
      setResourceOptions([])
    }
  }, [data])

  const resourceAlreadyAssigned =
    selectedResource?.assignedIncident === incident?.id

  const handleAssign = async () => {
    if (
      assigning ||
      !selectedResource ||
      resourceAlreadyAssigned ||
      isOffDuty(selectedResource)
    ) {
      return
    }

    setAssigning(true)
    setAssignError('')

    try {
      const res = await API.post('/commandcontrol/assignresourcetoincident', {
        assignedIncident: incident.id,
        controlRoomID,
        resourceID: selectedResource.id,
      })

      if (res.status !== 200) {
        // API never rejects on 4xx — guard refusals (e.g. resource Off Duty)
        // land here with a message
        setAssignError(
          res.data?.error || 'Could not assign the resource. Try again.',
        )
      } else {
        setSelectedIncident((prev) => {
          const alreadyExists = prev.assignedResources?.some(
            (r) => r.resourceId === selectedResource.id,
          )

          if (alreadyExists) return prev

          return {
            ...prev,
            assignedResources: [
              {
                resourceId: selectedResource.id,
                callSign: selectedResource.callSign,
                times: {
                  assigned: new Date(),
                },
                _id: res.data,
              },
              ...(prev.assignedResources || []),
            ],
          }
        })

        onClose()
      }
    } finally {
      setAssigning(false)
    }
  }

  return (
    <BigModal open={open} onClose={onClose} autoWidth>
      <Box sx={{ minWidth: '50vw' }}>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 6,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 30 }} />
            </Box>

            <Stack>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Resource Assignment
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                Assign Resource
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                Incident{' '}
                {incident?.incidentNumber?.length < 4
                  ? incident.incidentNumber
                  : `${String(incident?.incidentNumber ?? '').slice(0, -4)}-${String(incident?.incidentNumber ?? '').slice(-4)}`}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={2.5} padding={3}>
          {resourceOptions === null && (
            <LinearProgress
              sx={{
                borderRadius: 999,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#239AA4',
                },
              }}
            />
          )}

          <Autocomplete
            noOptionsText="No resources available"
            options={[...(resourceOptions || [])].sort((a, b) =>
              a.callSign.localeCompare(b.callSign),
            )}
            fullWidth
            value={selectedResource}
            onChange={(e, newValue) => setSelectedResource(newValue)}
            getOptionLabel={(opt) =>
              opt ? `${opt.callSign}${opt.type ? ` (${opt.type})` : ''}` : ''
            }
            isOptionEqualToValue={(option, value) =>
              option?.id === value?.id || option?._id === value?._id
            }
            getOptionDisabled={isOffDuty}
            renderOption={(props, opt, { index }) => {
              const statusStyle = statusStyles[opt?.status] || {
                bg: '#E6F7F9',
                color: '#1E7F88',
              }

              return (
                <li
                  {...props}
                  key={opt.id || opt._id}
                  style={{
                    ...props.style,
                    padding: '10px 14px',
                    borderBottom: '1px solid rgba(15,23,42,0.08)',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    spacing={2}
                  >
                    <Stack>
                      <Typography fontWeight={900} color="#242F40">
                        {opt?.callSign ?? ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(opt?.users ?? [])
                          .map((u) => `${u.name} (${u.clinicalRole})`)
                          .join(', ')}
                      </Typography>
                      {isOffDuty(opt) && (
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          color="#475569"
                        >
                          Off duty — change its status before assigning
                        </Typography>
                      )}
                    </Stack>

                    <Stack direction="row" gap={1} alignItems="center">
                      <Chip
                        size="small"
                        label={opt.type || 'Other'}
                        sx={{
                          borderRadius: 999,
                          backgroundColor: '#E6F7F9',
                          color: '#1E7F88',
                          fontWeight: 800,
                        }}
                      />

                      <Chip
                        size="small"
                        label={opt.status || 'Unknown'}
                        sx={{
                          borderRadius: 999,
                          minWidth: '7rem',
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 900,
                        }}
                      />
                    </Stack>
                  </Stack>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                fullWidth
                {...params}
                inputRef={resourceInputRef}
                label="Selected resource"
                required
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    borderRadius: 3,
                  },
                }}
              />
            )}
          />

          {selectedResource?.assignedIncident && !resourceAlreadyAssigned && (
            <Alert
              icon={<WarningAmberIcon />}
              severity="warning"
              sx={{
                borderRadius: 3,
                backgroundColor: '#FFF7ED',
                color: '#9A3412',
                border: '1px solid #FED7AA',
              }}
            >
              <strong>{selectedResource?.callSign}</strong> is currently
              assigned to another incident. Assigning them here will remove them
              from that incident.
            </Alert>
          )}

          {crossRoomConflict && !resourceAlreadyAssigned && (
            <Alert
              icon={<WarningAmberIcon />}
              severity="warning"
              sx={{
                borderRadius: 3,
                backgroundColor: '#FFF7ED',
                color: '#9A3412',
                border: '1px solid #FED7AA',
              }}
            >
              <strong>{selectedResource?.callSign}</strong>&rsquo;s crew is
              currently assigned to an incident in{' '}
              <strong>{crossRoomConflict.roomName}</strong>. Assigning them
              here will stand them down from that incident.
            </Alert>
          )}

          {unavailableStatuses.includes(selectedResource?.status) &&
            !resourceAlreadyAssigned && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 3,
                  backgroundColor: '#E6F7F9',
                  color: '#1E7F88',
                  border: '1px solid rgba(35,154,164,0.24)',
                }}
              >
                <strong>{selectedResource?.callSign}</strong> currently has the
                status <strong>{selectedResource?.status}</strong>. Assigning
                them will mark them available.
              </Alert>
            )}

          {resourceAlreadyAssigned && (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              <strong>{selectedResource?.callSign}</strong> is already assigned
              to this incident.
            </Alert>
          )}

          {assignError && (
            <Alert
              severity="error"
              onClose={() => setAssignError('')}
              sx={{ borderRadius: 3 }}
            >
              {assignError}
            </Alert>
          )}

          <Divider />

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
            <Button
              fullWidth
              onClick={onClose}
              disabled={assigning}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                color: '#64748B',
              }}
            >
              Cancel
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={handleAssign}
              disabled={
                !selectedResource ||
                resourceAlreadyAssigned ||
                assigning ||
                isOffDuty(selectedResource)
              }
              endIcon={
                assigning ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AssignmentTurnedInIcon />
                )
              }
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#239AA4',
                boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
                '&:hover': {
                  backgroundColor: '#1E7F88',
                  boxShadow: '0 16px 34px rgba(35,154,164,0.34)',
                },
              }}
            >
              Assign {selectedResource?.callSign ?? 'Resource'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </BigModal>
  )
}

export default AssignResource
